import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import { ConfirmDialog } from '../components/Dialog';
import { kurzTag } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import type { Waehrung } from './waehrung';
import { ZeilenMenue } from './ZeilenMenue';
import './formular.css';

export interface EntwurfZeile {
  id: string;
  art: 'rechnung' | 'angebot';
  // Kunde beim Speichern (leer: noch keiner)
  titel: string;
  // Betrag in der Währung des Entwurfs
  betrag: number;
  waehrung: Waehrung;
  geaendert: string;
  // „stundenzettel“: aus mehreren Stundenzetteln angelegt, „zeiten“: aus erfassten Zeiten (Billtoro); objekt: Einsatzort
  // im Entwurf (angezeigt nur bei diesen, dort unterscheidet er mehrere Entwürfe an denselben Kunden)
  herkunft?: string | null;
  objekt?: string | null;
  // Billtoro: abgelegte Stundenzettel aus dem Stapel, zettelWartend davon noch ohne Kunden (der Kunde entsteht erst
  // beim Schreiben); fehlt ohne Zettel
  zettel?: number;
  zettelWartend?: number;
}

// Was beim Löschen mit den Stundenzetteln geschieht: wartende gehen mit, die beim Kunden bleiben
function loeschText(e: EntwurfZeile | null): string {
  const t = 'Der Entwurf hat noch keine Nummer und ist danach weg.';
  if (!e?.zettel) return t;
  if (e.zettelWartend) return `${t} Die Stundenzettel dazu werden mit gelöscht, weil der Kunde noch nicht in Ihrer Kundenliste ist.`;
  return `${t} Die Stundenzettel bleiben beim Kunden unter Dateien.`;
}

const KURZ = 5;

// Entwürfe (halb fertig gespeichert, ohne Nummer) oben in der Rechnungsliste (für Angebote genauso): ruhige Zeilen im
// Muster der Rechnungsliste (rechnung.css, ul.rt-list.re-list.ew-list: Kunde | Betrag | Aktion). Die Zeile und der Knopf
// „Weiter schreiben“ öffnen das Formular, „Löschen“ steht im Menü „···“. Ohne Entwürfe zeigt der Baustein nichts.
// Props: firmId, art ('rechnung' | 'angebot'), today (YYYY-MM-DD, für „gespeichert am 22.10.“), weiter(id): Adresse des
// Formulars mit dem Entwurf (z. B. `/rechnung/neu?entwurf=${id}`), aktion: Knopf neben der Überschrift (z. B.
// „Stundenzettel hochladen“), aendern: weiter schreiben und löschen (Billtoro: Recht „Rechnungen“ bzw. „Angebote und
// Aufträge“; ohne nur die Liste).
export function Entwuerfe({
  firmId,
  art,
  today,
  weiter,
  aktion,
  aendern = true,
}: {
  firmId: string;
  art: EntwurfZeile['art'];
  today: string;
  weiter(id: string): string;
  aktion?: ReactNode;
  aendern?: boolean;
}) {
  const toast = useToast();
  const [list, setList] = useState<EntwurfZeile[]>([]);
  const [alle, setAlle] = useState(false);
  const [weg, setWeg] = useState<EntwurfZeile | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<{ entwuerfe: EntwurfZeile[] }>(`/api/rechnungstool/${firmId}/entwuerfe?art=${art}`)
      .then((r) => live && setList(r.entwuerfe))
      // ohne Entwürfe geht die Liste trotzdem
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [firmId, art]);

  async function loeschen() {
    if (!weg) return;
    setBusy(true);
    try {
      await api.del(`/api/rechnungstool/entwurf/${weg.id}`);
      setList((l) => l.filter((x) => x.id !== weg.id));
      toast('Entwurf gelöscht');
      setWeg(null);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!list.length) return null;
  const shown = alle ? list : list.slice(0, KURZ);
  const name = (e: EntwurfZeile) => e.titel || 'Ohne Kunde';
  // Herkunft in der grauen Zeile; bei Entwürfen aus Stundenzetteln oder Zeiten unterscheidet das Objekt (hinter dem
  // Kunden) mehrere Entwürfe an denselben Kunden
  const herkunft = (e: EntwurfZeile) => (e.herkunft === 'stundenzettel' ? 'aus Stundenzettel' : e.herkunft === 'zeiten' ? 'aus Zeiten' : null);
  return (
    <section className="ew" aria-label="Entwürfe">
      <div className="ew-kopf">
        <h2 className="ew-h">Entwürfe ({list.length})</h2>
        {aktion}
      </div>
      <ul className={`rt-list re-list ew-list${aendern ? '' : ' re-ohne-akt'}`}>
        {shown.map((e) => {
          const titel = `${name(e)}${herkunft(e) && e.objekt ? ` · ${e.objekt}` : ''}`;
          const inhalt = (
            <>
              <span className="re-kunde">
                <span className="rt-cust" title={titel}>
                  {titel}
                </span>
                <span className="rt-row-info">{[`gespeichert am ${kurzTag(e.geaendert, today)}`, herkunft(e)].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="rt-amt num">{e.betrag ? eurC(e.betrag, e.waehrung) : ''}</span>
            </>
          );
          return (
            <li key={e.id} className="rt-row">
              {aendern ? (
                <Link to={weiter(e.id)} className="rt-row-main" aria-label={`Entwurf ${name(e)} weiter schreiben`}>
                  {inhalt}
                </Link>
              ) : (
                <div className="rt-row-main">{inhalt}</div>
              )}
              {aendern && (
                <div className="re-act">
                  <div className="rt-next">
                    <Link to={weiter(e.id)} className="btn small">
                      Weiter schreiben
                    </Link>
                  </div>
                  <ZeilenMenue nummer={`Entwurf ${name(e)}`}>
                    <button type="button" role="menuitem" className="rt-danger" aria-label={`Entwurf ${name(e)} löschen`} onClick={() => setWeg(e)}>
                      Löschen
                    </button>
                  </ZeilenMenue>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {list.length > KURZ && (
        <button type="button" className="linkbtn ew-alle" onClick={() => setAlle(!alle)}>
          {alle ? 'Weniger zeigen' : `Alle ${list.length} Entwürfe zeigen`}
        </button>
      )}
      <ConfirmDialog
        open={!!weg}
        title={`Entwurf ${weg ? `„${name(weg)}“` : ''} löschen?`}
        text={loeschText(weg)}
        confirmLabel="Ja, löschen"
        danger
        busy={busy}
        onConfirm={() => void loeschen()}
        onCancel={() => setWeg(null)}
      />
    </section>
  );
}
